// frontend/src/pages/AdminTools.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { supabase } from "../lib/supabaseClient";

import { getRelevantQuestions } from "../lib/riskQuestionSet";
import { appendAuditSnapshot, rebuildAuditSnapshotIndexFromPayloads } from "../lib/auditHistory";

import {
  readEntitlementOverride,
  writeEntitlementOverride,
  clearEntitlementOverride,
} from "../lib/entitlementOverride";
import { usePlatformSecurityCapabilities } from "../context/PlatformSecurityCapabilityContext.jsx";

const STORAGE_PREFIX = "aifine_";

const STORAGE_KEYS = {
  USER: "aifine_user",
  ACTIVE_BUSINESS_ID: "aifine_active_business_id_v1",
  BUSINESS_INDEX: "aifine_business_index_v1",
  BUSINESS_PAYLOAD_PREFIX: "aifine_business_payload_",
  CONFIG: "aifine_config",
  ANSWERS_PREFIX: "aifine_answers_business_",
  PROGRESS_PREFIX: "aifine_assessment_progress_business_",
};

const STANDARD_SINGLE_ID = "single_default";

function safeParse(raw, fallback = null) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function clamp(n, lo, hi) {
  const v = Number(n);
  if (!Number.isFinite(v)) return lo;
  return Math.max(lo, Math.min(hi, v));
}

function getActiveBusinessId() {
  try {
    return localStorage.getItem(STORAGE_KEYS.ACTIVE_BUSINESS_ID) || "";
  } catch {
    return "";
  }
}

function loadBusinessPayload(businessId) {
  try {
    return safeParse(localStorage.getItem(`${STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX}${businessId}`), null);
  } catch {
    return null;
  }
}

function loadConfigForBusiness(businessId) {
  const payload = businessId ? loadBusinessPayload(businessId) : null;
  const fallback = safeParse(localStorage.getItem(STORAGE_KEYS.CONFIG), null);
  return payload || fallback || null;
}

function setConfigShim(payload) {
  try {
    localStorage.setItem(STORAGE_KEYS.CONFIG, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

function setBusinessPayload(businessId, payload) {
  try {
    if (!businessId || !payload || typeof payload !== "object") return false;
    localStorage.setItem(`${STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX}${businessId}`, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

function setActiveBusinessId(businessId) {
  try {
    localStorage.setItem(STORAGE_KEYS.ACTIVE_BUSINESS_ID, String(businessId));
    return true;
  } catch {
    return false;
  }
}


function normalizeStatus(value) {
  const s = String(value || "").trim().toLowerCase();
  if (!s) return "unknown";
  if (["active", "active_one_time", "trialing"].includes(s)) return "active";
  if (["inactive", "canceled", "cancelled", "past_due", "unpaid"].includes(s)) return "inactive";
  return s;
}

function toIso(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function addDaysIso(value, days) {
  const iso = toIso(value);
  if (!iso) return null;
  const d = new Date(iso);
  d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString();
}

function formatDateTime(value) {
  const iso = toIso(value);
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function toCsv(rows) {
  const cols = [
    "user_id",
    "plan_key",
    "status",
    "status_normalized",
    "marketing_opt_in",
    "marketing_opt_in_source",
    "marketing_opt_in_at",
    "canceled_at",
    "retention_expires_at",
    "retention_basis",
    "created_at",
    "updated_at",
  ];

  const esc = (v) => {
    const s = v == null ? "" : String(v);
    if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };

  const lines = [cols.join(",")];
  for (const row of Array.isArray(rows) ? rows : []) {
    lines.push(cols.map((c) => esc(row?.[c])).join(","));
  }
  return lines.join("\n");
}

function downloadCsv(filename, content) {
  try {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return true;
  } catch {
    return false;
  }
}

function countAnsweredValues(obj) {
  if (!obj || typeof obj !== "object") return 0;
  let n = 0;
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v === true || v === false || v === "unsure" || v === "YES" || v === "NO" || v === "UNSURE") n += 1;
  }
  return n;
}

function randPick(weights) {
  const total = weights.reduce((acc, x) => acc + Math.max(0, Number(x.w) || 0), 0);
  if (total <= 0) return weights[0]?.v;

  let r = Math.random() * total;
  for (const item of weights) {
    r -= Math.max(0, Number(item.w) || 0);
    if (r <= 0) return item.v;
  }
  return weights[weights.length - 1]?.v;
}

function writeLocalUserPatch(patch = {}) {
  try {
    const prev = safeParse(localStorage.getItem(STORAGE_KEYS.USER), {}) || {};
    const next = {
      ...prev,
      ...patch,
      updated_at_iso: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(next));
  } catch {
    // ignore
  }
}

function readTierForScope() {
  const ov = readEntitlementOverride();
  if (ov?.tier) return String(ov.tier).toLowerCase() === "top" ? "top" : "single";

  const user = safeParse(localStorage.getItem(STORAGE_KEYS.USER), null);
  const t = user?.subscription_tier ?? user?.subscriptionTier ?? user?.tier ?? null;
  return String(t || "single").toLowerCase() === "top" ? "top" : "single";
}

function getDashboardScopeBusinessId() {
  const tier = readTierForScope();
  if (tier === "top") return getActiveBusinessId() || "";
  return STANDARD_SINGLE_ID;
}

function ensureTestingBusinessContext({ businessId, configPayload }) {
  const bid = String(businessId || "").trim() || STANDARD_SINGLE_ID;
  const cfg = configPayload && typeof configPayload === "object" ? configPayload : null;

  setActiveBusinessId(bid);

  if (cfg) {
    setConfigShim(cfg);
    setBusinessPayload(bid, cfg);
  }

  return bid;
}

export default function AdminTools() {
  const navigate = useNavigate();

  const {
    status: capabilityStatus,
    hasCapability,
    error: capabilityError,
  } = usePlatformSecurityCapabilities();

  const authz = {
    loading: capabilityStatus === "idle" || capabilityStatus === "loading",
    allowed:
      capabilityStatus === "ready" &&
      hasCapability("MANAGE_PLATFORM_SECURITY"),
    error: capabilityError,
  };
  const [status, setStatus] = useState(null);

  const [yesPct, setYesPct] = useState(60);
  const [noPct, setNoPct] = useState(25);
  const [unsurePct, setUnsurePct] = useState(15);

  const [seedRuns, setSeedRuns] = useState(6);
  const [seedMin, setSeedMin] = useState(45);
  const [seedMax, setSeedMax] = useState(92);

  const [subscriberRows, setSubscriberRows] = useState([]);
  const [subscriberLoading, setSubscriberLoading] = useState(false);
  const [subscriberError, setSubscriberError] = useState("");
  const [subscriberNotice, setSubscriberNotice] = useState("");
  const [subscriberSearch, setSubscriberSearch] = useState("");
  const [subscriberFilter, setSubscriberFilter] = useState("all");

  const businessId = useMemo(() => {
    const bid = getDashboardScopeBusinessId();
    return bid || STANDARD_SINGLE_ID;
  }, []);

  const cfg = useMemo(() => loadConfigForBusiness(businessId), [businessId]);

  const questionCount = useMemo(() => {
    try {
      const qs = getRelevantQuestions(cfg);
      return Array.isArray(qs) ? qs.length : 0;
    } catch {
      return 0;
    }
  }, [cfg]);

  const override = useMemo(() => readEntitlementOverride(), []);

  useEffect(() => {
    if (!authz.allowed) return;

    let alive = true;

    async function loadSubscriberVisibility() {
      setSubscriberLoading(true);
      setSubscriberError("");
      setSubscriberNotice("");

      try {
        let { data, error } = await supabase
          .from("subscriptions")
          .select(
            "user_id, status, plan_key, created_at, updated_at, marketing_opt_in, marketing_opt_in_source, marketing_opt_in_at, canceled_at"
          )
          .order("updated_at", { ascending: false });

        if (error && String(error?.message || "").includes("marketing_opt_in")) {
          const fallback = await supabase
            .from("subscriptions")
            .select("user_id, status, plan_key, created_at, updated_at, canceled_at")
            .order("updated_at", { ascending: false });
          data = fallback.data;
          error = fallback.error;
          if (!error && alive) {
            setSubscriberNotice("Subscriber rows loaded. Optional marketing-consent columns are not present in this DEV schema, so consent fields are shown as unavailable.");
          }
        }

        if (error) throw error;
        if (!alive) return;

        const rows = (Array.isArray(data) ? data : []).map((row) => {
          const normalized = normalizeStatus(row?.status);
          const basis = row?.canceled_at ? "explicit canceled_at" : normalized === "inactive" ? "inferred from updated_at" : "not applicable";
          const baseCancel = row?.canceled_at || (normalized === "inactive" ? row?.updated_at : null);
          const retentionExpiresAt = baseCancel ? addDaysIso(baseCancel, 60) : null;
          const withinRetention = Boolean(retentionExpiresAt && new Date(retentionExpiresAt).getTime() > Date.now());

          return {
            ...row,
            consent_available: Object.prototype.hasOwnProperty.call(row || {}, "marketing_opt_in"),
            status_normalized: normalized,
            retention_basis: basis,
            retention_expires_at: retentionExpiresAt,
            within_retention_window: withinRetention,
          };
        });

        setSubscriberRows(rows);
      } catch (err) {
        if (!alive) return;
        setSubscriberRows([]);
        setSubscriberError(String(err?.message || err || "Unable to load subscriber visibility."));
      } finally {
        if (alive) setSubscriberLoading(false);
      }
    }

    loadSubscriberVisibility();

    return () => {
      alive = false;
    };
  }, [authz.allowed]);

  const setMessage = (msg) => {
    setStatus({ msg, t: Date.now() });
  };

  const clearLocalTestData = () => {
    const ok = window.confirm(
      "Clear ALL local test data?\n\nThis will remove localStorage keys starting with 'aifine_' (including local business configs, in-progress answers, and saved report snapshots). It will NOT delete anything in Supabase.\n\nContinue?"
    );
    if (!ok) return;

    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (String(k).startsWith(STORAGE_PREFIX)) keys.push(k);
      }
      keys.forEach((k) => localStorage.removeItem(k));

      setMessage(`Cleared ${keys.length} local keys.`);
    } catch {
      setMessage("Clearing local keys failed (storage error).");
    }
  };

  const autopopulateAnswers = (mode = "all") => {
    const effectiveCfg = cfg || safeParse(localStorage.getItem(STORAGE_KEYS.CONFIG), null) || null;

    if (!effectiveCfg) {
      setMessage("No active config found (run Start Audit first).");
      return;
    }

    const effectiveBusinessId = ensureTestingBusinessContext({
      businessId,
      configPayload: effectiveCfg,
    });

    let qs = [];
    try {
      qs = getRelevantQuestions(effectiveCfg);
    } catch {
      qs = [];
    }

    if (!Array.isArray(qs) || qs.length === 0) {
      setMessage("No questions found for current config.");
      return;
    }

    const y = clamp(yesPct, 0, 100);
    const n = clamp(noPct, 0, 100);
    const u = clamp(unsurePct, 0, 100);

    const weights = [
      { v: true, w: y },
      { v: false, w: n },
      { v: "unsure", w: u },
    ];

    const answersKey = `${STORAGE_KEYS.ANSWERS_PREFIX}${effectiveBusinessId}`;
    const progressKey = `${STORAGE_KEYS.PROGRESS_PREFIX}${effectiveBusinessId}`;

    const existing = safeParse(localStorage.getItem(answersKey), {});
    const next = existing && typeof existing === "object" ? { ...existing } : {};

    const total = qs.length;
    const fillCount = mode === "partial" ? Math.max(1, Math.floor(total * 0.35)) : total;

    for (let i = 0; i < fillCount; i += 1) {
      const q = qs[i];
      const id = q?.id;
      if (!id) continue;
      next[id] = randPick(weights);
    }

    try {
      localStorage.setItem(answersKey, JSON.stringify(next));
    } catch {
      setMessage("Failed writing answers (storage error).");
      return;
    }

    try {
      const completedCount = countAnsweredValues(next);
      const currentIndex = mode === "partial" ? Math.max(0, fillCount - 1) : 0;

      localStorage.setItem(
        progressKey,
        JSON.stringify({
          version: 1,
          business_id: effectiveBusinessId,
          currentIndex,
          totalQuestions: total,
          completedCount,
          updated_at_iso: new Date().toISOString(),
        })
      );
    } catch {
      // ignore
    }

    setMessage(
      mode === "partial"
        ? `Autofilled ~35% of answers for ${total} questions. Open /assessment to confirm it resumes.`
        : `Autofilled answers for ${total} questions. Open /assessment to confirm.`
    );
  };

  const applyTierOverride = (tier) => {
    const clean = String(tier || "").toLowerCase() === "top" ? "top" : "single";

    const next = {
      version: 1,
      tier: clean,
      isPaid: true,
      rawPlanKey: clean === "top" ? "override_top" : "override_single",
      allowReports: true,
      allowRemediation: true,
      allowConsultantWorkflow: clean === "top",
      maxBusinesses: clean === "top" ? 25 : 1,
      updated_at_iso: new Date().toISOString(),
    };

    const ok = writeEntitlementOverride(next);
    if (!ok) {
      setMessage("Failed writing override (storage error).");
      return;
    }

    writeLocalUserPatch({
      subscription_tier: clean,
      raw_tier: next.rawPlanKey,
    });

    setMessage(`Applied local entitlement override: ${clean.toUpperCase()}. Reloading…`);
    window.setTimeout(() => window.location.reload(), 650);
  };

  const clearTierOverride = () => {
    clearEntitlementOverride();
    setMessage("Cleared local entitlement override. Reloading…");
    window.setTimeout(() => window.location.reload(), 450);
  };

  const seedTrend = () => {
    const effectiveCfg = cfg || safeParse(localStorage.getItem(STORAGE_KEYS.CONFIG), null) || null;

    if (!effectiveCfg) {
      setMessage("No active config found (run Start Audit first).");
      return;
    }

    const effectiveBusinessId = ensureTestingBusinessContext({
      businessId,
      configPayload: effectiveCfg,
    });

    const runs = clamp(seedRuns, 2, 25);
    const lo = clamp(seedMin, 0, 100);
    const hi = clamp(seedMax, 0, 100);
    const min = Math.min(lo, hi);
    const max = Math.max(lo, hi);

    const entityName = String(effectiveCfg?.clientName || "Demo Business");
    const jurs = Array.isArray(effectiveCfg?.jurisdictions) ? effectiveCfg.jurisdictions : [];

    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;

    try {
      for (let i = 0; i < runs; i += 1) {
        const t = now - (runs - 1 - i) * day;

        const score = Math.round(min + Math.random() * (max - min));
        const totalQuestions = Math.max(1, questionCount || 60);
        const yesCount = Math.round((score / 100) * totalQuestions);
        const failedCount = Math.max(0, totalQuestions - yesCount);
        const unsureCount = Math.round(failedCount * 0.2);

        appendAuditSnapshot({
          snapshot_id: `${t}`,
          business_id: effectiveBusinessId,
          entity_name: entityName,
          generated_at: new Date(t).toISOString(),
          summary: { jurisdictions: jurs },
          assessment: {
            compliance_score_pct: score,
            total_questions: totalQuestions,
            failed_count: failedCount,
            unsure_count: unsureCount,
            failed_question_ids: [],
            unsure_question_ids: [],
            active_question_ids: [],
          },
        });
      }

      try {
        rebuildAuditSnapshotIndexFromPayloads();
      } catch {
        // ignore
      }

      setMessage(`Seeded ${runs} snapshot(s) for Dashboard trend. Go to Dashboard and refresh once.`);
    } catch (err) {
      setMessage(`Seed Trend failed: ${String(err?.message || err || "unknown error")}`);
    }
  };

  const openAssessmentForTesting = () => {
    const effectiveCfg = cfg || safeParse(localStorage.getItem(STORAGE_KEYS.CONFIG), null) || null;

    if (!effectiveCfg) {
      setMessage("No active config found (run Start Audit first).");
      return;
    }

    ensureTestingBusinessContext({
      businessId,
      configPayload: effectiveCfg,
    });

    navigate("/assessment");
  };

  const subscriberSummary = useMemo(() => {
    const rows = Array.isArray(subscriberRows) ? subscriberRows : [];
    return {
      total: rows.length,
      active: rows.filter((r) => r.status_normalized === "active").length,
      inactive: rows.filter((r) => r.status_normalized === "inactive").length,
      optedIn: rows.filter((r) => r.marketing_opt_in === true).length,
      within60Days: rows.filter((r) => r.within_retention_window === true).length,
    };
  }, [subscriberRows]);

  const filteredSubscriberRows = useMemo(() => {
    const q = String(subscriberSearch || "").trim().toLowerCase();
    return (Array.isArray(subscriberRows) ? subscriberRows : []).filter((row) => {
      if (subscriberFilter === "active" && row.status_normalized !== "active") return false;
      if (subscriberFilter === "inactive" && row.status_normalized !== "inactive") return false;
      if (subscriberFilter === "opted_in" && row.marketing_opt_in !== true) return false;
      if (subscriberFilter === "within_60" && row.within_retention_window !== true) return false;

      if (!q) return true;

      const hay = [
        row.user_id,
        row.plan_key,
        row.status,
        row.status_normalized,
        row.marketing_opt_in_source,
        row.retention_basis,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");

      return hay.includes(q);
    });
  }, [subscriberRows, subscriberSearch, subscriberFilter]);

  const exportSubscriberRows = () => {
    const ok = downloadCsv(
      `subscriber_visibility_${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(filteredSubscriberRows)
    );

    setMessage(ok ? `Exported ${filteredSubscriberRows.length} row(s) to CSV.` : "CSV export failed.");
  };

  if (authz.loading) {
    return (
      <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-auto">
          <div className="mx-auto max-w-4xl px-8 py-10">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              Checking admin access…
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!authz.allowed) {
    return (
      <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-auto">
          <div className="mx-auto max-w-4xl px-8 py-10">
            <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6">
              <div className="text-sm font-black text-rose-900">Not authorized</div>
              <div className="mt-2 text-sm text-rose-900/80">
                This page requires the MANAGE_PLATFORM_SECURITY capability.
              </div>
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Back to Dashboard →
                </button>
              </div>
              {authz.error ? (
                <div className="mt-6 text-xs text-rose-900/70">
                  Platform Security capability check failed: {authz.error}
                </div>
              ) : null}
            </div>

            <div className="mt-10 pb-8 text-xs text-slate-400">
              © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
      <Sidebar />

      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-5xl px-8 py-10">
          <div className="flex items-start justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Super Admin Tools</h1>
              <p className="mt-1 text-sm text-slate-600">
                Local development utilities (safe to use; affects localStorage only).
              </p>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                  Dashboard Scope Business ID: {businessId}
                </span>
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                  Questions in current config: {questionCount || "—"}
                </span>
                {override?.tier ? (
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-black text-amber-900">
                    Override: {String(override.tier).toUpperCase()}
                  </span>
                ) : (
                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                    Override: none
                  </span>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Back to Dashboard →
            </button>
          </div>

          {status?.msg ? (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
              {status.msg}
            </div>
          ) : null}

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Local reset</div>
              <div className="mt-2 text-xl font-extrabold tracking-tight text-slate-900">Clear local test data</div>
              <div className="mt-1 text-sm text-slate-600">
                Removes <span className="font-mono">aifine_*</span> keys from localStorage.
              </div>

              <div className="mt-5">
                <button
                  type="button"
                  onClick={clearLocalTestData}
                  className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
                >
                  Clear ALL local test data
                </button>
              </div>
            </div>

            <div className="lg:col-span-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Entitlement</div>
              <div className="mt-2 text-xl font-extrabold tracking-tight text-slate-900">Switch subscriber type</div>
              <div className="mt-1 text-sm text-slate-600">
                Sets a LOCAL override for UI gating and multi-business testing.
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => applyTierOverride("single")}
                  aria-pressed={override?.tier === "single"}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold ${override?.tier === "single" ? "bg-slate-900 text-white ring-2 ring-slate-300" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
                >
                  {override?.tier === "single" ? "Single Tier — Selected" : "Set Single Tier"}
                </button>
                <button
                  type="button"
                  onClick={() => applyTierOverride("top")}
                  aria-pressed={override?.tier === "top"}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold ${override?.tier === "top" ? "bg-slate-900 text-white ring-2 ring-slate-300" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
                >
                  {override?.tier === "top" ? "Top Tier — Selected" : "Set Top Tier"}
                </button>
                <button
                  type="button"
                  onClick={clearTierOverride}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Clear Override
                </button>
              </div>
            </div>

            <div className="lg:col-span-7 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Assessment</div>
              <div className="mt-2 text-xl font-extrabold tracking-tight text-slate-900">
                Auto-populate assessment answers
              </div>
              <div className="mt-1 text-sm text-slate-600">
                Writes randomized answers into localStorage for the Dashboard-scope business.
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">Yes %</div>
                  <input
                    type="number"
                    value={yesPct}
                    onChange={(e) => setYesPct(clamp(e.target.value, 0, 100))}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
                  />
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">No %</div>
                  <input
                    type="number"
                    value={noPct}
                    onChange={(e) => setNoPct(clamp(e.target.value, 0, 100))}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
                  />
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">Unsure %</div>
                  <input
                    type="number"
                    value={unsurePct}
                    onChange={(e) => setUnsurePct(clamp(e.target.value, 0, 100))}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => autopopulateAnswers("all")}
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Fill ALL questions
                </button>
                <button
                  type="button"
                  onClick={() => autopopulateAnswers("partial")}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Fill ~35% (in-progress)
                </button>
                <button
                  type="button"
                  onClick={openAssessmentForTesting}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Open Assessment →
                </button>
              </div>
            </div>

            <div className="lg:col-span-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Dashboard</div>
              <div className="mt-2 text-xl font-extrabold tracking-tight text-slate-900">Seed trend chart</div>
              <div className="mt-1 text-sm text-slate-600">
                Creates local audit snapshots so the trend chart populates.
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">Runs</div>
                  <input
                    type="number"
                    value={seedRuns}
                    onChange={(e) => setSeedRuns(clamp(e.target.value, 2, 25))}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
                  />
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">Min %</div>
                  <input
                    type="number"
                    value={seedMin}
                    onChange={(e) => setSeedMin(clamp(e.target.value, 0, 100))}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
                  />
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">Max %</div>
                  <input
                    type="number"
                    value={seedMax}
                    onChange={(e) => setSeedMax(clamp(e.target.value, 0, 100))}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={seedTrend}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Seed Trend
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  View Dashboard →
                </button>
              </div>
            </div>
          </div>


          <div className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Subscriber Visibility</div>
                <div className="mt-2 text-xl font-extrabold tracking-tight text-slate-900">No-email subscriber visibility + CSV export</div>
                <div className="mt-1 max-w-3xl text-sm text-slate-600">
                  This safe view reads only subscription fields that the current frontend session can already access. It does not expose email addresses, does not bypass Supabase policies, and infers the 60-day window from <span className="font-mono">canceled_at</span> or, for inactive rows without it, from <span className="font-mono">updated_at</span>.
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={exportSubscriberRows}
                  disabled={subscriberLoading || filteredSubscriberRows.length === 0}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Export CSV
                </button>
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              {[
                ["Visible rows", subscriberSummary.total],
                ["Active", subscriberSummary.active],
                ["Inactive", subscriberSummary.inactive],
                ["Opted in", subscriberSummary.optedIn],
                ["Within 60 days", subscriberSummary.within60Days],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-400">{label}</div>
                  <div className="mt-2 text-2xl font-black text-slate-900">{value}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <input
                type="text"
                value={subscriberSearch}
                onChange={(e) => setSubscriberSearch(e.target.value)}
                placeholder="Search by user ID, plan, status, or source"
                className="min-w-[280px] flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-900"
              />

              <div className="flex flex-wrap gap-2">
                {[
                  ["all", "All"],
                  ["active", "Active"],
                  ["inactive", "Inactive"],
                  ["opted_in", "Opted In"],
                  ["within_60", "Within 60 Days"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setSubscriberFilter(value)}
                    className={`rounded-full px-3 py-1.5 text-xs font-black ${subscriberFilter === value ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {subscriberNotice ? (
              <div className="mt-5 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                {subscriberNotice}
              </div>
            ) : null}

            {subscriberError ? (
              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Could not load subscriber visibility from the browser session. Current Supabase access or schema does not support this safe read. Error: {subscriberError}
              </div>
            ) : null}

            {subscriberLoading ? (
              <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">Loading subscriber visibility…</div>
            ) : null}

            <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[11px] font-black uppercase tracking-[0.25em] text-slate-500">
                    <th className="px-4 py-3">User ID</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Consent</th>
                    <th className="px-4 py-3">Consent Source</th>
                    <th className="px-4 py-3">Canceled At</th>
                    <th className="px-4 py-3">Retention Expires</th>
                    <th className="px-4 py-3">Basis</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {!subscriberLoading && filteredSubscriberRows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-6 text-sm text-slate-500">
                        No visible rows for the current browser session and filter settings.
                      </td>
                    </tr>
                  ) : null}

                  {filteredSubscriberRows.map((row) => (
                    <tr key={`${row.user_id || "no-user"}-${row.created_at || row.updated_at || Math.random()}`} className="align-top">
                      <td className="px-4 py-3 text-xs font-semibold text-slate-700">
                        <div className="max-w-[240px] break-all">{row.user_id || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{row.plan_key || "—"}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-black ${row.status_normalized === "active" ? "bg-emerald-100 text-emerald-800" : row.status_normalized === "inactive" ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-700"}`}>
                          {row.status || row.status_normalized || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-slate-700">{row.consent_available ? (row.marketing_opt_in === true ? "Yes" : "No") : "Unavailable"}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{row.marketing_opt_in_source || "—"}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{formatDateTime(row.canceled_at)}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{formatDateTime(row.retention_expires_at)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{row.retention_basis || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-10 pb-8 text-xs text-slate-400">
            © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
          </div>
        </div>
      </main>
    </div>
  );
}
